import { RiAddLine, RiDeleteBinLine } from '@remixicon/react';
import { set } from 'lodash';
import { useState } from 'react';
import { AgButton, AgInput, AgModal, AgModalActions, AgModalContent, AgText } from 'src/components/ui';
import { useUpdateWarehouse } from './hooks/useUpdateWarehouse';

type Props = { open: boolean; tagKeys: string[]; onClose: () => void; warehouseId: number };

export default function LocationGroupingsModal({ open, tagKeys, onClose, warehouseId }: Props) {
  const [localTagKeys, setLocalTagKeys] = useState<string[]>(tagKeys.length ? tagKeys : ['']);

  const { update: updateWarehouse, loading } = useUpdateWarehouse();

  const save = async () => {
    const tagKeys = localTagKeys.map((tag) => tag.trim()).filter(Boolean);
    await updateWarehouse({ id: warehouseId, input: { tagKeys } });
    onClose();
  };

  const editTag = (index: number, value: string) => {
    setLocalTagKeys((prev) => {
      const updated = [...prev];
      set(updated, index, value);
      return updated;
    });
  };

  const addTag = () => {
    setLocalTagKeys((prev) => [...prev, '']);
  };

  const removeTag = (index: number) => {
    if (localTagKeys.length === 1) {
      setLocalTagKeys(['']);
    } else {
      setLocalTagKeys(localTagKeys.filter((_, i) => i !== index));
    }
  };

  return (
    <AgModal open={open} onClose={onClose} title="Edit Location Groupings" size="xs">
      <AgModalContent>
        <AgText size="14" color="secondary" className="mb-3">
          Create groups to organize your warehouse locations. These will become columns in your locations table, and you
          can create as many as you need.
        </AgText>

        <div className="flex flex-col gap-4">
          {localTagKeys.map((key, i) => (
            <div key={i} className="flex items-center justify-between gap-2">
              <AgInput
                value={key}
                onChange={(value) => editTag(i, value)}
                placeholder="e.g., Room, Section, Level"
                isDisabled={loading}
                className="grow"
              />
              <AgButton variant="ghost" size="icon-sm" onPress={() => removeTag(i)} isDisabled={loading}>
                <RiDeleteBinLine size={20} />
              </AgButton>
            </div>
          ))}
          <div>
            <AgButton
              onPress={addTag}
              isDisabled={loading || !localTagKeys[localTagKeys.length - 1].trim()}
              variant="primary"
            >
              <RiAddLine size={20} />
              Add Group
            </AgButton>
          </div>
        </div>
      </AgModalContent>

      <AgModalActions>
        <AgButton variant="primary" onPress={save} isDisabled={loading}>
          Save Groupings
        </AgButton>
      </AgModalActions>
    </AgModal>
  );
}
